/**
 * `row_drill_mode` — how a table shows the detail of a clicked row.
 *
 * Two modes the author CHOOSES per report (product decision 2026-09-09,
 * after the meeting), mirrored in the engine
 * (`report_engine.models.ROW_DRILL_MODES`) and validated on BOTH sides:
 *
 *   * `"swap"` (default when the config omits the field) — the cell's
 *     content is replaced by the drill panel with the directional slide
 *     (`ViewSlide`, PR #54 design) and "Back" restores the table. It is
 *     the default because it is what every published report already
 *     does: a missing field changes nothing in production.
 *   * `"accordion"` (explicit opt-in) — the detail expands UNDER the
 *     clicked row, inside the table's own scrolling rows zone. The row
 *     stays visible, what grows is the scrollable content; no view
 *     change, so `ViewSlide` does not run here.
 *
 * In BOTH the grid cell keeps its exact size. Anything else is a config
 * error. The render surfaces it as a visible error card
 * (`DashboardTable`), the engine refuses to load the report
 * (`discovery._parse_table`) and the builder rejects the draft at
 * validation (422) — the value never degrades to either mode silently.
 */

export const ROW_DRILL_MODES = ["accordion", "swap"] as const;

export type RowDrillMode = (typeof ROW_DRILL_MODES)[number];

/** The mode a table gets when its config does not name one — the swap,
 *  i.e. what every report published before the field existed does. */
export const DEFAULT_ROW_DRILL_MODE: RowDrillMode = "swap";

export class RowDrillModeError extends Error {
  readonly value: unknown;
  constructor(value: unknown) {
    super(
      `row_drill_mode ${JSON.stringify(value)} is invalid — must be one of ${ROW_DRILL_MODES.join(", ")}`,
    );
    this.name = "RowDrillModeError";
    this.value = value;
  }
}

/**
 * Resolve the authored value to a mode. `undefined` / `null` / `""`
 * mean "not authored" and take the documented default; a present value
 * must be one of `ROW_DRILL_MODES` exactly (case-sensitive, like every
 * other enum the engine compares verbatim) or this THROWS.
 */
export function resolveRowDrillMode(raw: unknown): RowDrillMode {
  if (raw === undefined || raw === null || raw === "") {
    return DEFAULT_ROW_DRILL_MODE;
  }
  if (typeof raw === "string" && (ROW_DRILL_MODES as readonly string[]).includes(raw)) {
    return raw as RowDrillMode;
  }
  throw new RowDrillModeError(raw);
}
